class ElxWaterPurifierPurchase extends HTMLElement {
  connectedCallback() {
    this.mediaTabs = Array.from(this.querySelectorAll('[data-elx-media-tab]'));
    this.mediaPanels = Array.from(this.querySelectorAll('[data-elx-media-panel]'));
    this.optionCards = Array.from(this.querySelectorAll('[data-elx-purchase-option]'));
    this.bundleTemplate = this.querySelector('[data-elx-bundle-template]');
    this.imageButtons = Array.from(this.querySelectorAll('[data-elx-image-button]'));
    this.imageSlides = Array.from(this.querySelectorAll('[data-elx-image-slide]'));
    this.imagePrevious = this.querySelector('[data-elx-image-prev]');
    this.imageNext = this.querySelector('[data-elx-image-next]');
    this.thumbnailViewport = this.querySelector('[data-elx-thumbnail-viewport]');
    this.thumbnailTrack = this.querySelector('.elx-water-purifier__thumbnails');
    this.thumbnailPrevious = this.querySelector('[data-elx-thumbnail-prev]');
    this.thumbnailNext = this.querySelector('[data-elx-thumbnail-next]');
    this.quantityInput = this.querySelector('[data-elx-quantity]');
    this.quantityDecrease = this.querySelector('[data-elx-quantity-decrease]');
    this.quantityIncrease = this.querySelector('[data-elx-quantity-increase]');
    this.addButton = this.querySelector('[data-elx-add]');
    this.dynamicCheckout = this.querySelector('[data-elx-dynamic-checkout]');
    this.dynamicCheckoutVariant = this.querySelector('[data-elx-dynamic-checkout-variant]');
    this.filterAddButtons = Array.from(this.querySelectorAll('[data-elx-filter-add]'));
    this.compareAddButtons = Array.from(this.querySelectorAll('[data-elx-compare-add]'));
    this.status = this.querySelector('[data-elx-status]');
    this.compareSliders = Array.from(this.querySelectorAll('[data-elx-compare-range]'));
    this.header = null;
    this.story = null;
    this.storyNav = null;
    this.hero = this.querySelector('.elx-water-purifier__hero-grid');
    this.details = this.querySelector('.elx-water-purifier__details-primary');
    this.mediaTrack = this.querySelector('.elx-water-purifier__media-track');
    this.mediaPin = this.querySelector('.elx-water-purifier__media-pin');
    this.mediaColumn = this.querySelector('.elx-water-purifier__media-column');
    this.mediaStickyBoundary = this.querySelector('.elx-water-purifier__media-sticky-boundary');
    this.mediaFollowup = this.querySelector('.elx-water-purifier__media-followup');
    this.followupGrid = this.querySelector('.elx-water-purifier__followup-grid');
    this.service = this.querySelector('.elx-water-purifier__service');
    this.purchaseOverviewToggle = this.querySelector('[data-elx-purchase-overview-toggle]');
    this.purchaseOverviewPanel = this.querySelector('[data-elx-purchase-overview-panel]');
    this.headerLockedForStory = false;

    // Old edge-cached section HTML can retain this removed visual-only note.
    this.querySelectorAll('.elx-water-purifier__plan-note').forEach((note) => note.remove());

    this.normalizeMediaFlow();
    this.mediaTabs.forEach((tab) => tab.addEventListener('click', () => this.selectMediaTab(tab.dataset.elxMediaTab)));
    this.imageButtons.forEach((button) => button.addEventListener('click', () => this.selectImage(button.dataset.elxImageButton)));
    this.imagePrevious?.addEventListener('click', () => this.selectImage(this.currentImageIndex - 1));
    this.imageNext?.addEventListener('click', () => this.selectImage(this.currentImageIndex + 1));
    this.thumbnailPrevious?.addEventListener('click', () => this.scrollThumbnails(-1));
    this.thumbnailNext?.addEventListener('click', () => this.scrollThumbnails(1));
    this.thumbnailViewport?.addEventListener('scroll', () => this.syncThumbnailControls(), { passive: true });
    this.optionCards.forEach((card) => this.bindPurchaseOption(card));
    this.quantityDecrease?.addEventListener('click', () => this.stepQuantity(-1));
    this.quantityIncrease?.addEventListener('click', () => this.stepQuantity(1));
    this.quantityInput?.addEventListener('change', () => {
      this.normalizeQuantity();
      this.syncDynamicCheckout();
    });
    this.quantityInput?.addEventListener('input', () => this.syncDynamicCheckout());
    this.addButton?.addEventListener('click', () => this.addSelected());
    this.filterAddButtons.forEach((button) => button.addEventListener('click', () => this.addFilter(button)));
    this.compareAddButtons.forEach((button) => button.addEventListener('click', () => this.addComparisonProduct(button)));
    this.purchaseOverviewToggle?.addEventListener('click', () => this.togglePurchaseOverview());
    this.compareSliders.forEach((slider) => {
      const sync = () => {
        const wrapper = slider.closest('[data-elx-compare]');
        wrapper?.style.setProperty('--elx-compare', slider.value);
        wrapper?.style.setProperty('--compare', slider.value);
      };
      slider.addEventListener('input', sync);
      sync();
    });

    this.setupHeaderBehavior();
    this.setupMediaPinning();
    this.setupStoryNavigation();
    this.setupDetailComparison();
    this.setPurchaseOverviewExpanded(this.purchaseOverviewToggle?.getAttribute('aria-expanded') !== 'false');
    this.selectPurchaseOption(this.querySelector('[data-elx-purchase-option][aria-checked="true"]') || this.optionCards[0]);
    this.selectImage('0');
    this.syncThumbnailControls();
    this.hydrateBundleOption();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onHeaderScroll);
    window.removeEventListener('resize', this.onHeaderResize);
    window.removeEventListener('resize', this.onMediaResize);
    window.removeEventListener('scroll', this.onStoryScroll);
    this.headerResizeObserver?.disconnect();
    this.mediaResizeObserver?.disconnect();
    if (this.headerScrollFrame) window.cancelAnimationFrame(this.headerScrollFrame);
    if (this.storyScrollFrame) window.cancelAnimationFrame(this.storyScrollFrame);

    if (!document.querySelector('elx-water-purifier:not([data-section-id="' + this.dataset.sectionId + '"])')) {
      this.header?.classList.remove('elx-water-purifier-header--hidden');
      document.body.classList.remove('elx-water-purifier-active');
      document.documentElement.style.removeProperty('--elx-water-purifier-header-offset');
      document.documentElement.style.removeProperty('--elx-water-purifier-story-offset');
      this.hero?.style.removeProperty('min-height');
      this.hero?.style.removeProperty('--elx-water-purifier-media-height');
      this.hero?.style.removeProperty('--elx-water-purifier-media-sticky-travel');
    }
  }

  normalizeMediaFlow() {
    if (this.details && this.service && this.service.parentElement !== this.details) {
      this.details.append(this.service);
    }

    const desktop = window.matchMedia('(min-width: 750px)').matches;
    const hasFilter = Boolean(this.mediaFollowup?.querySelector('.elx-water-purifier__filter'));
    if (this.mediaFollowup) this.mediaFollowup.hidden = !hasFilter;

    if (desktop && hasFilter && this.mediaColumn && this.mediaStickyBoundary && this.mediaFollowup) {
      if (this.mediaFollowup.parentElement !== this.mediaColumn) this.mediaColumn.append(this.mediaFollowup);
    } else {
      if (this.followupGrid && this.mediaFollowup && this.mediaFollowup.parentElement !== this.followupGrid) {
        this.followupGrid.prepend(this.mediaFollowup);
      }
    }

    if (this.followupGrid) {
      const hasVisibleChild = Array.from(this.followupGrid.children).some((child) => !child.hidden);
      this.followupGrid.hidden = desktop ? !hasVisibleChild : !hasFilter;
    }
  }

  getDetailsContentHeight() {
    if (!this.details) return 0;
    const detailsBox = this.details.getBoundingClientRect();
    const serviceBox = this.service?.getBoundingClientRect();
    if (serviceBox && this.details.contains(this.service)) {
      return Math.max(0, serviceBox.bottom - detailsBox.top);
    }
    return Math.max(0, this.details.scrollHeight);
  }

  scrollThumbnails(direction) {
    if (!this.thumbnailViewport) return;
    const distance = Math.max(240, this.thumbnailViewport.clientWidth * 0.8);
    this.thumbnailViewport.scrollBy({ left: direction * distance, behavior: 'smooth' });
  }

  syncThumbnailControls() {
    if (!this.thumbnailViewport || !this.thumbnailTrack) return;
    const maxScroll = Math.max(0, this.thumbnailTrack.scrollWidth - this.thumbnailViewport.clientWidth);
    const currentScroll = this.thumbnailViewport.scrollLeft;
    const hasOverflow = maxScroll > 2;
    if (this.thumbnailPrevious) {
      this.thumbnailPrevious.disabled = !hasOverflow || currentScroll <= 2;
      this.thumbnailPrevious.hidden = !hasOverflow;
    }
    if (this.thumbnailNext) {
      this.thumbnailNext.disabled = !hasOverflow || currentScroll >= maxScroll - 2;
      this.thumbnailNext.hidden = !hasOverflow;
    }
  }

  setupMediaPinning() {
    if (!this.mediaTrack) return;

    this.syncMediaPinning = () => {
      this.normalizeMediaFlow();
      if (!window.matchMedia('(min-width: 750px)').matches) {
        this.removeAttribute('data-elx-media-static');
        return;
      }

      const mediaStackHeight = this.mediaColumn?.getBoundingClientRect().height || this.mediaTrack.getBoundingClientRect().height;
      const detailsContentHeight = this.getDetailsContentHeight();
      // CSS pins the complete media stack and its grid row ends with the purchase details.
      this.toggleAttribute('data-elx-media-static', mediaStackHeight >= detailsContentHeight - 1);
    };

    this.onMediaResize = () => this.syncMediaPinning();
    this.syncMediaPinning();
    window.addEventListener('resize', this.onMediaResize);
    if ('ResizeObserver' in window) {
      this.mediaResizeObserver?.disconnect();
      this.mediaResizeObserver = new ResizeObserver(this.onMediaResize);
      this.mediaResizeObserver.observe(this.mediaTrack);
      if (this.mediaFollowup) this.mediaResizeObserver.observe(this.mediaFollowup);
      if (this.details) this.mediaResizeObserver.observe(this.details);
      if (this.service) this.mediaResizeObserver.observe(this.service);
    }
  }

  setPurchaseOverviewExpanded(expanded) {
    if (!this.purchaseOverviewToggle || !this.purchaseOverviewPanel) return;
    this.purchaseOverviewToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.purchaseOverviewPanel.classList.toggle('is-collapsed', !expanded);
  }

  togglePurchaseOverview() {
    this.setPurchaseOverviewExpanded(this.purchaseOverviewToggle?.getAttribute('aria-expanded') !== 'true');
  }

  setupHeaderBehavior() {
    document.body.classList.add('elx-water-purifier-active');
    this.header = document.querySelector('#header-component') || document.querySelector('header-component');
    this.lastScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);

    this.onHeaderScroll = () => {
      if (this.headerScrollFrame) return;
      this.headerScrollFrame = window.requestAnimationFrame(() => {
        this.headerScrollFrame = null;
        this.updateHeaderVisibility();
      });
    };
    this.onHeaderResize = () => {
      if (!window.matchMedia('(min-width: 750px)').matches) {
        this.headerLockedForStory = false;
        this.header?.classList.remove('elx-water-purifier-header--hidden');
      }
      this.syncThumbnailControls();
      this.syncMediaPinning?.();
      this.updateHeaderVisibility();
    };

    window.addEventListener('scroll', this.onHeaderScroll, { passive: true });
    window.addEventListener('resize', this.onHeaderResize);
    if (this.header && 'ResizeObserver' in window) {
      this.headerResizeObserver = new ResizeObserver(() => this.syncHeaderOffset());
      this.headerResizeObserver.observe(this.header);
    }

    this.updateHeaderVisibility();
  }

  updateHeaderVisibility() {
    const currentY = Math.max(0, window.scrollY || window.pageYOffset || 0);
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    if (!this.header || !desktop) {
      if (!desktop) this.headerLockedForStory = false;
      this.header?.classList.remove('elx-water-purifier-header--hidden');
      this.lastScrollY = currentY;
      this.syncHeaderOffset();
      return;
    }

    const delta = currentY - this.lastScrollY;
    const headerHeight = this.header.getBoundingClientRect().height || this.header.offsetHeight || 0;
    if (currentY < 24) this.headerLockedForStory = false;
    if (!this.headerLockedForStory && this.isStoryNavigationPinned()) {
      this.headerLockedForStory = true;
    }

    const canHide = currentY > Math.max(130, headerHeight + 20) && !this.headerHasOpenMenu();
    if (this.headerLockedForStory || (delta > 8 && canHide)) {
      this.header.classList.add('elx-water-purifier-header--hidden');
    } else if (delta < -8 || currentY < 24) {
      this.header.classList.remove('elx-water-purifier-header--hidden');
    }

    this.lastScrollY = currentY;
    this.syncHeaderOffset();
    this.syncMediaPinning?.();
    this.updateStoryNavigation();
  }

  isStoryNavigationPinned() {
    if (!this.story || !this.storyNav) return false;
    const storyOffset = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--elx-water-purifier-story-offset')) || 0;
    return this.story.getBoundingClientRect().top <= storyOffset + 1;
  }

  headerHasOpenMenu() {
    return Boolean(this.header?.querySelector('[aria-expanded="true"], details[open]'));
  }

  syncHeaderOffset() {
    const isHidden = this.header?.classList.contains('elx-water-purifier-header--hidden');
    const offset = isHidden ? 0 : Math.ceil(this.header?.getBoundingClientRect().height || this.header?.offsetHeight || 0);
    const value = offset + 'px';
    this.style.setProperty('--elx-water-purifier-header-offset', value);
    document.documentElement.style.setProperty('--elx-water-purifier-header-offset', value);
    document.documentElement.style.setProperty('--elx-water-purifier-story-offset', value);
  }

  setupStoryNavigation() {
    this.story = document.querySelector('[data-elx-water-purifier-details]');
    this.storyNav = this.story?.querySelector('[data-elx-details-nav]') || null;
    const navInner = this.storyNav?.querySelector('.elx-water-purifier-details__nav-inner');
    if (!this.story || !this.storyNav || !navInner) return;

    if (!navInner.querySelector('[data-elx-story-product]')) {
      const identity = document.createElement('div');
      identity.className = 'elx-water-purifier__story-product';
      identity.dataset.elxStoryProduct = 'true';

      if (this.dataset.elxProductImage) {
        const image = document.createElement('img');
        image.className = 'elx-water-purifier__story-product-image';
        image.src = this.dataset.elxProductImage;
        image.alt = '';
        image.width = 48;
        image.height = 48;
        identity.append(image);
      }

      const title = document.createElement('span');
      title.className = 'elx-water-purifier__story-product-title';
      title.textContent = this.dataset.elxProductTitle || document.title;
      identity.append(title);
      navInner.prepend(identity);
    }

    this.storyLinks = Array.from(navInner.querySelectorAll('a[href^="#"]'));
    this.storyLinks.forEach((link) => link.dataset.elxStoryNavLink = 'true');
    this.storySections = this.storyLinks
      .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
      .filter(Boolean);

    this.onStoryScroll = () => {
      if (this.storyScrollFrame) return;
      this.storyScrollFrame = window.requestAnimationFrame(() => {
        this.storyScrollFrame = null;
        this.updateStoryNavigation();
      });
    };
    window.addEventListener('scroll', this.onStoryScroll, { passive: true });
    this.updateHeaderVisibility();
  }


  setupDetailComparison() {
    this.detailCompareSliders = Array.from(document.querySelectorAll('[data-elx-details-compare-range]'));
    this.detailCompareSliders.forEach((slider) => {
      const sync = () => {
        const wrapper = slider.closest('[data-elx-compare]');
        wrapper?.style.setProperty('--compare', slider.value);
      };
      slider.addEventListener('input', sync);
      sync();
    });
  }

  updateStoryNavigation() {
    if (!this.storyNav || !this.storyLinks?.length || !this.storySections?.length) return;
    const headerOffset = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--elx-water-purifier-story-offset')) || 0;
    const marker = headerOffset + this.storyNav.getBoundingClientRect().height + 28;
    let active = this.storySections[0];
    this.storySections.forEach((section) => {
      if (section.getBoundingClientRect().top <= marker) active = section;
    });

    this.storyLinks.forEach((link) => {
      const isActive = link.hash === '#' + active.id;
      if (isActive) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }

  selectMediaTab(name) {
    this.mediaTabs.forEach((tab) => {
      const selected = tab.dataset.elxMediaTab === name;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
    this.mediaPanels.forEach((panel) => {
      panel.hidden = panel.dataset.elxMediaPanel !== name;
    });
  }

  selectPurchaseOption(card) {
    if (!card) return;
    this.optionCards.forEach((item) => item.setAttribute('aria-checked', item === card ? 'true' : 'false'));
    this.selectedOption = card;
    this.syncDynamicCheckout();
  }

  syncDynamicCheckout() {
    if (!this.dynamicCheckout) return;
    const option = this.selectedOption;
    const isBundle = Boolean(option?.hasAttribute('data-elx-bundle-option'));
    this.dynamicCheckout.hidden = isBundle;
    this.dynamicCheckout.setAttribute('aria-hidden', isBundle ? 'true' : 'false');
    if (this.dynamicCheckoutVariant && option?.dataset.mainVariantId) {
      this.dynamicCheckoutVariant.value = option.dataset.mainVariantId;
    }
  }

  bindPurchaseOption(card) {
    card.addEventListener('click', () => {
      this.selectPurchaseOption(card);
    });
    card.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectPurchaseOption(card);
      }
    });
  }

  formatMoney(cents) {
    const numericValue = Number(cents);
    if (!Number.isFinite(numericValue)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.elxCurrency || 'USD',
      }).format(numericValue / 100);
    } catch {
      return `$${(numericValue / 100).toFixed(2)}`;
    }
  }

  hydrateBundleOption() {
    const filterButtons = Array.from(this.querySelectorAll('[data-elx-filter-add]'));
    if (!filterButtons.length || !this.bundleTemplate) return;

    try {
      const filters = filterButtons.map((button) => this.resolveBundleFilter(button));
      if (filters.some((filter) => !filter)) return;
      this.renderBundleOption(filters);
    } catch {
      // A malformed filter price leaves the one-time purchase path intact.
    }
  }

  resolveBundleFilter(button) {
    const variantId = String(button.dataset.filterVariantId || '');
    const regularPrice = Number(button.dataset.filterVariantPrice || 0);
    if (!variantId || !Number.isFinite(regularPrice) || regularPrice < 0) return null;

    return {
      image: button.dataset.elxFilterImage || '',
      regularPrice,
      title: button.dataset.elxFilterTitle || 'Replacement filter',
      variantId,
    };
  }

  renderBundleOption(filters) {
    const fragment = this.bundleTemplate.content.cloneNode(true);
    const option = fragment.querySelector('[data-elx-bundle-option]');
    if (!option) return;

    const mainPrice = Number(this.dataset.elxMainVariantPrice || 0);
    const filterTotal = filters.reduce((total, filter) => total + filter.regularPrice, 0);
    const bundlePrice = mainPrice + filterTotal;

    option.dataset.filterItems = JSON.stringify(filters.map((filter) => ({
      variantId: filter.variantId,
      quantity: 1,
    })));
    option.querySelector('[data-elx-bundle-price]')?.replaceChildren(this.formatMoney(bundlePrice));

    const filterList = option.querySelector('[data-elx-bundle-filter-list]');
    const rowTemplate = option.querySelector('[data-elx-bundle-filter-row]');
    if (filterList && rowTemplate) {
      filterList.replaceChildren();
      filters.forEach((filter) => {
        const row = rowTemplate.cloneNode(true);
        row.querySelector('[data-elx-bundle-filter-title]')?.replaceChildren(filter.title);
        row.querySelector('[data-elx-bundle-filter-price]')?.replaceChildren(this.formatMoney(filter.regularPrice));

        const media = row.querySelector('[data-elx-bundle-filter-media]');
        if (media && filter.image) {
          const image = new Image();
          image.src = filter.image;
          image.alt = filter.title;
          image.loading = 'lazy';
          image.width = 220;
          image.height = 220;
          media.replaceChildren(image);
        }
        filterList.append(row);
      });
    }

    this.bundleTemplate.replaceWith(fragment);
    this.bundleTemplate = null;
    this.optionCards = Array.from(this.querySelectorAll('[data-elx-purchase-option]'));
    this.bindPurchaseOption(option);
    // Keep the Liquid-selected one-time purchase active until the customer chooses the bundle.
    window.requestAnimationFrame(() => this.syncMediaPinning?.());
  }

  selectImage(index) {
    const count = this.imageSlides.length;
    if (!count) return;
    const numericIndex = Number(index);
    this.currentImageIndex = (Number.isFinite(numericIndex) ? numericIndex : 0) % count;
    if (this.currentImageIndex < 0) this.currentImageIndex += count;
    const selectedIndex = String(this.currentImageIndex);
    this.imageButtons.forEach((button) => {
      const selected = button.dataset.elxImageButton === selectedIndex;
      button.setAttribute('aria-current', selected ? 'true' : 'false');
    });
    const selectedSlide = this.imageSlides.find((slide) => slide.dataset.elxImageSlide === selectedIndex);
    this.imageSlides.forEach((slide) => {
      slide.hidden = slide !== selectedSlide;
    });
    const ratio = Number(selectedSlide?.dataset.elxImageRatio);
    if (Number.isFinite(ratio) && ratio > 0) {
      this.style.setProperty('--elx-water-purifier-active-media-ratio', String(ratio));
    } else {
      this.style.removeProperty('--elx-water-purifier-active-media-ratio');
    }
    this.ensureThumbnailVisible(this.imageButtons.find((button) => button.dataset.elxImageButton === selectedIndex));
    this.syncThumbnailControls();
    this.syncMediaPinning?.();
  }

  ensureThumbnailVisible(button) {
    if (!button || !this.thumbnailViewport) return;
    const start = button.offsetLeft;
    const end = start + button.offsetWidth;
    const visibleStart = this.thumbnailViewport.scrollLeft;
    const visibleEnd = visibleStart + this.thumbnailViewport.clientWidth;
    let next = visibleStart;
    if (start < visibleStart) next = start;
    if (end > visibleEnd) next = end - this.thumbnailViewport.clientWidth;
    if (Math.abs(next - visibleStart) < 2) return;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.thumbnailViewport.scrollTo({
      left: Math.max(0, next),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  async addSelected() {
    const option = this.selectedOption;
    if (!option) return;
    const quantity = this.normalizeQuantity();
    const items = [];

    if (option.dataset.mainVariantId) {
      items.push({ id: Number(option.dataset.mainVariantId), quantity });
    }

    const filterItems = this.parseBundleItems(option);
    if (filterItems.length) {
      filterItems.forEach((filter) => {
        const filterItem = { id: Number(filter.variantId), quantity };
        items.push(filterItem);
      });
    } else if (option.dataset.filterVariantId) {
      const filterItem = { id: Number(option.dataset.filterVariantId), quantity: Number(option.dataset.filterQuantity || '1') || 1 };
      items.push(filterItem);
    }

    if (!items.length) return;
    await this.addItems(items);
  }

  parseBundleItems(option) {
    try {
      const value = JSON.parse(option.dataset.filterItems || '[]');
      return Array.isArray(value)
        ? value.filter((item) => item && item.variantId)
        : [];
    } catch {
      return [];
    }
  }

  async addFilter(button) {
    const variantId = Number(button.dataset.filterVariantId);
    if (!variantId) return;
    const item = { id: variantId, quantity: 1 };
    await this.addItems([item], button);
  }

  async addComparisonProduct(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;
    await this.addItems([{ id: variantId, quantity: 1 }], button);
  }

  async addItems(items, sourceButton = null) {
    if (!items.length) return;

    this.setBusy(true, sourceButton);
    try {
      const addItemsToCart = window.ElxCartActions?.addItemsToCart;
      if (typeof addItemsToCart !== 'function') throw new Error('Shopping cart is still loading. Please try again.');

      await addItemsToCart({
        items,
        sourceId: this.id || this.dataset.sectionId,
        source: 'elx-water-purifier-purchase',
        productId: this.dataset.productId || null,
      });
      this.announce('Added to cart.');
    } catch (error) {
      this.announce(error.message || 'Unable to add to cart.');
    } finally {
      this.setBusy(false, sourceButton);
    }
  }

  stepQuantity(step) {
    const current = this.normalizeQuantity();
    if (this.quantityInput) this.quantityInput.value = String(Math.max(1, current + step));
    this.syncDynamicCheckout();
  }

  normalizeQuantity() {
    if (!this.quantityInput) return 1;
    const value = Math.min(99, Math.max(1, Number.parseInt(this.quantityInput.value || '1', 10) || 1));
    this.quantityInput.value = String(value);
    return value;
  }

  setBusy(isBusy, sourceButton = null) {
    this.addButton?.toggleAttribute('disabled', isBusy);
    sourceButton?.toggleAttribute('disabled', isBusy);
    this.addButton?.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('elx-water-purifier')) {
  customElements.define('elx-water-purifier', ElxWaterPurifierPurchase);
}
